import { useState, useRef, useEffect } from "react";
import { useSingleton } from "../../hooks/use-singleton";
import { useApi } from "../../hooks/use-api";
import {
  API_COUNT_MCQ,
  API_GET_MCQ,
  API_JOIN_ROOM,
  API_LEARNING_CHALLENGE_UPDATE,
  API_MEDIUMS,
  API_SUBJECTS,
  apiAsyncSubmit,
  apiCodeJoinResults,
  apiJoinRoomHistory,
  apiLeaderBoard,
  apiRoomAsyncQuestions,
} from "../../utils/api-routes";
import {
  API_MCQ_SUBJECTS,
  API_MCQ_CHAPTERS,
  API_EVALUATE,
} from "../../utils/api-routes";
import { apiStudentStudys } from "../../utils/api-routes";
import { useSearchParams, useLocation } from "react-router-dom";

const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  let trimmed = url
    .trim()
    .replace(/(%22|%27|["',\\\/])+$/gi, "")
    .replace(/^(%22|%27|["'\\\/])+/gi, "")
    .trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return "";
  }
  // If it's already a full URL from the question API, return it directly!
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:image/") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }
  if (trimmed.startsWith("question_assets/")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${trimmed}`;
  } else if (trimmed.startsWith("/question_assets/")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
  } else if (trimmed.includes("question_assets")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${assetPart}`;
  } else if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
  }
  return trimmed;
};

const isValidImageUrl = (url) => Boolean(sanitizeImageUrl(url));
const getValidOptImg = (img) => {
  if (!img) return null;
  if (typeof img === "object") {
    img = img.url || img.src || img.image_url || img.image || "";
  }
  if (typeof img !== "string" || !img.trim()) return null;
  return sanitizeImageUrl(img) || img;
};

const isImgUrl = (str) => {
  if (!str || typeof str !== "string") return false;
  const s = str.trim();
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:image/") ||
    s.startsWith("blob:") ||
    s.startsWith("/") ||
    s.includes("question_assets") ||
    /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(s)
  );
};

const useHandler = () => {
  const { user, mail, medium: medi, section } = useSingleton();
  const { get, post } = useApi();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get("subject");
  const chapter = searchParams.get("chapter");
  const medium = searchParams.get("medium");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(
    subject ? subject : "",
  );
  const [selectedChapter, setSelectedChapter] = useState(
    chapter ? chapter : "",
  );
  const [selectedMedium, setSelectedMedium] = useState(medium ? medium : "");
  const [mediums, setMediums] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [questionDetails, setQuestionDetails] = useState([]);
  const [markDetails, setMarkDetails] = useState({});
  const [showMarks, setShowMarks] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectChapters, setSelectedChapters] = useState(
    chapter ? [chapter] : [],
  );
  const [selectedQuestion, setSelectedQuestion] = useState("");

  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [numberOfQuestion, setNumberOfQuestion] = useState([]);
  const [hidePanals, setHidePanals] = useState(false);
  const [mode, setMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0); // 15 minutes 42 seconds in seconds
  const [showPaletteModal, setShowPaletteModal] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showSetupPopup, setShowSetupPopup] = useState(false);

  // new feature
  const [roomDetails, setRoomDetails] = useState({
    roomCode: "",
    nickName: "",
  });
  const [quizInitial, setQuizInitial] = useState(true);
  const [joinQuiz, setJoinQuiz] = useState(false);
  const [waitingCall, setWaitingCall] = useState(false);
  const [socket, setSocket] = useState(null);
  const [players, setPlayers] = useState([]);
  const [isQuizStarted, setIsQuizStarted] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const code = location.state?.roomCode || location.state?.room_code;
    if (code) {
      setRoomDetails((prev) => ({
        ...prev,
        roomCode: String(code),
      }));
      setQuizInitial(false);
      setJoinQuiz(true);
    }
  }, [location.state]);

  // websocketstates
  const [wsQuestion, setWsQuestion] = useState(null);
  const [wsQuestionIndex, setWsQuestionIndex] = useState(0);
  const [wsTotalQuestions, setWsTotalQuestions] = useState(0);
  const [wsLeaderboard, setWsLeaderboard] = useState([]);
  const [wsFinalScores, setWsFinalScores] = useState([]);
  const [wsParticipants, setWsParticipants] = useState([]);
  const [wsQuizStarted, setWsQuizStarted] = useState(false);
  const [wsQuizEnded, setWsQuizEnded] = useState(false);
  const [wsSelectedAnswer, setWsSelectedAnswer] = useState(null);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [showCompletedScreen, setShowCompletedScreen] = useState(false);
  const [wsReviewMode, setWsReviewMode] = useState(false);
  const [wsQuizSubmitted, setWsQuizSubmitted] = useState(false);
  const [wsQuizFinalResults, setWsQuizFinalResults] = useState([]);
  const [wsSubmitted, setWsSubmitted] = useState(false);
  const [showAdminRoundResult, setShowAdminRoundResult] = useState(false);
  const [adminRoundResult, setAdminRoundResult] = useState([]);

  // async
  const [isAsyncQuiz, setIsAsyncQuiz] = useState(false);
  const [asyncQuestions, setAsyncQuestions] = useState([]);
  const [asyncCurrentIndex, setAsyncCurrentIndex] = useState(0);
  const [asyncSelectedAnswers, setAsyncSelectedAnswers] = useState({});
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [asyncLeaderboard, setAsyncLeaderboard] = useState([]);
  const [showAsyncLeaderboard, setShowAsyncLeaderboard] = useState(false);
  const [quizHistorySessions, setQuizHistorySessions] = useState([]);
  const [showAsyncHistory, setShowAsyncHistory] = useState(false);
  const [wsTimeRemaining, setWsTimeRemaining] = useState(15);
  const asyncQuestionStartTimeRef = useRef(null);
  const asyncQuestionTimesRef = useRef({});

  const { roomCode, nickName } = roomDetails;

  const mediumRef = useRef(null);
  const subjectRef = useRef(null);
  const chapterRef = useRef(null);
  const questionRef = useRef(null);
  const selectedAnswersRef = useRef(selectedAnswers);
  const socketRef = useRef(null);

  const [panelOpen, setPanelOpen] = useState(true); // Start open

  // const isStartEnabled = !!selectedSubject && !!selectedChapter;

  const isStartEnabled =
    !!selectedSubject && selectChapters.length > 0 && selectedQuestion;

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        const payload = {
          board: res?.data?.data?.board,
          clas: res?.data?.data?.class,
        };
        getMediumDetails(payload);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  const getMediumDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MEDIUMS, payload);
      if (res) {
        setMediums(res?.data?.mediums);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getSubjectDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_SUBJECTS, payload);
      if (res) {
        setSubjects(res?.data?.subjects);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getChapterDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MCQ_CHAPTERS, payload);
      if (res) {
        setChapters(res?.data?.chapters);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting chapeter details", e);
    }
  };

  const getNoQuestionsDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await get(API_COUNT_MCQ, payload);
      if (res) {
        setNumberOfQuestion(res?.data?.count);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting chapeter details", e);
    }
  };

  const getMcqdetails = async () => {
    try {
      const payload = {
        mail,
        board: user?.data?.board,
        clas: user?.data?.class,
        subject: selectedSubject,
        // chapter:selectedChapter,
        chapter: selectChapters,
        // medium: selectedMedium,
        medium: medi,
        count: selectedQuestion,
        section,
      };
      const res = await post(API_GET_MCQ, payload);
      if (res) {
        setQuestionDetails(res?.data);
        setPanelOpen(true);
      }
    } catch (e) {
      console.log("getting error from get mcq details", e);
      const status = e?.response?.status;

      if (status === 403) {
        setHidePanals(false);
        setIsQuizActive(false);
        setQuestionDetails([]);
      }
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  useEffect(() => {
    getNoQuestionsDetails();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mediumRef.current?.contains(e.target) ||
        subjectRef.current?.contains(e.target) ||
        chapterRef.current?.contains(e.target) ||
        questionRef.current?.contains(e.target)
      )
        return;

      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onStartQuiz = () => getMcqdetails();

  useEffect(() => {
    if (!medi || !user?.data?.class) return;
    const payload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      // medium: selectedMedium,
      medium: medi,
    };

    getSubjectDetails(payload);
  }, [medi, user]);

  useEffect(() => {
    selectedAnswersRef.current = selectedAnswers;
  }, [selectedAnswers]);

  useEffect(() => {
    if (!selectedSubject || !user?.data?.class) return;

    setSelectedChapters([]);

    const payload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      subject: selectedSubject,
      // medium: selectedMedium,
      medium: medi,
    };
    getChapterDetails(payload);
  }, [selectedSubject]);

  const normalizeQuestions = (apiData = []) =>
    apiData.map((q) => {
      const rawImages = Array.isArray(q.IMAGE_URLS)
        ? q.IMAGE_URLS
        : Array.isArray(q.image_urls)
        ? q.image_urls
        : Array.isArray(q.imageUrls)
        ? q.imageUrls
        : q.IMAGE_URL || q.image_url || q.image || q.diagram
        ? [q.IMAGE_URL || q.image_url || q.image || q.diagram]
        : [];
      const imageUrls = rawImages.map(sanitizeImageUrl).filter(Boolean);

      const rawOptImg = q.OPTION_IMAGES || q.option_images || {};
      const optionImages = {
        A: getValidOptImg(rawOptImg.a || rawOptImg.A || q["OPTION A_IMAGE"] || q.option_a_image || q["OPTION A IMAGE"] || q.option_a_img),
        B: getValidOptImg(rawOptImg.b || rawOptImg.B || q["OPTION B_IMAGE"] || q.option_b_image || q["OPTION B IMAGE"] || q.option_b_img),
        C: getValidOptImg(rawOptImg.c || rawOptImg.C || q["OPTION C_IMAGE"] || q.option_c_image || q["OPTION C IMAGE"] || q.option_c_img),
        D: getValidOptImg(rawOptImg.d || rawOptImg.D || q["OPTION D_IMAGE"] || q.option_d_image || q["OPTION D IMAGE"] || q.option_d_img),
        E: getValidOptImg(rawOptImg.e || rawOptImg.E || q["OPTION E_IMAGE"] || q.option_e_image || q["OPTION E IMAGE"] || q.option_e_img),
      };

      const options = [
        q["OPTION A"] || q.option_a || q.optionA || "",
        q["OPTION B"] || q.option_b || q.optionB || "",
        q["OPTION C"] || q.option_c || q.optionC || "",
        q["OPTION D"] || q.option_d || q.optionD || "",
      ];
      if (q["OPTION E"] || q.option_e || q.optionE) {
        options.push(q["OPTION E"] || q.option_e || q.optionE);
      }

      return {
        question: q["QUESTION"] || q.question || q.question_text || "",
        options,
        answer: q["ANSWER"] || q.answer || "",
        questionId: q["QUESTION_ID"] || q.id || q.question_id || "",
        imageUrls,
        optionImages,
      };
    });

  const currentQuestion = questions[currentQIndex];

  useEffect(() => {
    if (questionDetails?.length) {
      const formatted = normalizeQuestions(questionDetails);
      setQuestions(formatted);
    }
  }, [questionDetails]);

  const handleOptionSelect = (index, qIndex) => {
    const targetIndex = qIndex !== undefined ? qIndex : currentQIndex;
    // ❌ if already answered, don't allow change
    if (!mode && selectedAnswers[targetIndex] !== undefined) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [targetIndex]: index,
    }));
  };

  const selectedOption = selectedAnswers[currentQIndex];

  const handleNext = () => {
    if (currentQIndex < questions.length - 1) {
      setCurrentQIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQIndex > 0) {
      setCurrentQIndex((prev) => prev - 1);
    }
  };

  const buildSubmitPayload = () => {
    return questions
      .map((q, index) => {
        // const selectedIndex = selectedAnswers[index];
        const selectedIndex = selectedAnswersRef.current[index];

        // // Skip unattempted questions
        // if (selectedIndex === undefined) return null;

        return {
          QUESTION: q.question,
          ANSWER: selectedIndex !== undefined ? q.options[selectedIndex] : "",
          CHAPTER: selectedChapter,
          QUESTION_ID: q.questionId || "",
        };
      })
      .filter(Boolean); // remove nulls
  };

  const unansweredCount = questions?.filter(
    (_, i) => selectedAnswers[i] === undefined,
  ).length;

  const answerdQuestions = Object.keys(selectedAnswers).length;

  const learningChallengeProgress = async () => {
    const attempted = Object.keys(selectedAnswers).length;
    if (attempted <= 0) return;

    const payload = {
      email: mail,
      action_type: "quiz",
      watch_percentage: 100,
      questions_attempted: attempted,
      study_minutes: 0,
    };
    try {
      const res = await post(API_LEARNING_CHALLENGE_UPDATE, payload);
      if (res) {
        console.log(res);
      }
    } catch (e) {
      console.log("ERROR COMES FROM LEARNING CHALLANGE API", e);
    }
  };

  const onSubmitQuiz = async () => {
    const payload = buildSubmitPayload();
    const formattedPayload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      subject: selectedSubject,
      mail,
      answers: payload,
      // medium: selectedMedium,
      medium: medi,
      section,
    };
    setShowMarks(true);
    setSubmitLoading(true);

    try {
      const res = await post(API_EVALUATE, formattedPayload);
      if (res) {
        console.log(res?.data, "res");
        setMarkDetails(res?.data);
        localStorage.setItem("marks", res?.data);
        setPanelOpen(false);
        setSubmitLoading(false);
        await learningChallengeProgress();
      }
    } catch (e) {
      setSubmitLoading(false);
      console.log("Error on submitquiz details", e);
    }
  };

  const isAnswered = selectedOption !== undefined;

  const isCorrect =
    isAnswered &&
    currentQuestion?.options[selectedOption] === currentQuestion?.answer;

  const onCancel = () => {
    setIsQuizActive(false);
    setShowMarks(false);
    setMarkDetails({});
    setQuestionDetails([]);
    setSelectedSubject("");
    setSelectedChapter("");
    setSelectedChapters([]);
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setHidePanals(false);
    setSubmitLoading(false);
    setIsReviewMode(false);
    setQuestions([]);
    setTimeRemaining(0);
    setSelectedQuestion("");
  };

  const resetQuizState = () => {
    setIsQuizActive(true);
    setShowMarks(false);
    setMarkDetails({});
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setPanelOpen(true);
    setHidePanals(true);
    setSubmitLoading(false);
  };

  const onRetakeQuiz = async () => {
    resetQuizState();

    // Option 1: reuse already fetched questions
    if (questionDetails?.length) {
      const formatted = normalizeQuestions(questionDetails);
      setQuestions(formatted);
      return;
    }

    // Option 2: re-fetch questions from API (recommended)
    // await getMcqdetails();
  };

  const getQuizDuration = (questionCount) => {
    // if (questionCount <= 10) return 10 * 60;   // 10 min
    // if (questionCount <= 20) return 15 * 60;   // 15 min
    // if (questionCount <= 25) return 20 * 60;   // 20 min
    // return 25 * 60; // fallback if more questions in future
    return questionCount * 60;
  };

  const onToggleQuizBtn = () => {
    setMode((p) => !p);
    // setSelectedSubject("");
    // setSelectedChapters([]);
  };

  const getQuestionStatus = (index) => {
    if (index === currentQIndex) return "current";

    if (selectedAnswers[index] !== undefined) {
      return "answered";
    }

    if (index < currentQIndex) {
      return "not-answered";
    }

    return "not-visited";
  };

  // new futrue
  const onChangeRoom = (e) => {
    const { name, value } = e.target;
    setRoomDetails((prev) => ({ ...prev, [name]: value }));
  };

  const connectWebSocket = (roomCode, nickName) => {
    const ws = new WebSocket(
      `wss://rovianai.com/secure-api/room/ws/${roomCode}?role=participant&mail=${encodeURIComponent(mail)}`,
    );

    socketRef.current = ws;

    ws.onopen = () => {
      console.log("✅ Connected to WebSocket");

      // Send join event
      ws.send(
        JSON.stringify({
          type: "join_room",
          room_code: roomCode,
          nickname: nickName,
        }),
      );
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      console.log("📩 WebSocket Data:", data);

      switch (data.type || data.event) {
        case "player_list":
          setPlayers(data.players);
          break;

        case "user_joined":
          setPlayers((prev) => [...prev, data.user]);
          break;

        case "start_quiz":
        case "quiz_started":
          setWsQuizStarted(true);
          setWaitingCall(false);
          break;

        case "question": {
          const rawImageUrls = Array.isArray(data.image_urls)
            ? data.image_urls
            : Array.isArray(data.IMAGE_URLS)
            ? data.IMAGE_URLS
            : data.image_url || data.IMAGE_URL
            ? [data.image_url || data.IMAGE_URL]
            : [];
          const sanitizedImageUrls = rawImageUrls.map(sanitizeImageUrl).filter(Boolean);

          const optImgs = data.option_images || data.OPTION_IMAGES || data.optionImages || {};
          const sanitizedOptionImages = {
            A: getValidOptImg(optImgs.A || optImgs.a || data["OPTION A_IMAGE"] || data.option_a_image),
            B: getValidOptImg(optImgs.B || optImgs.b || data["OPTION B_IMAGE"] || data.option_b_image),
            C: getValidOptImg(optImgs.C || optImgs.c || data["OPTION C_IMAGE"] || data.option_c_image),
            D: getValidOptImg(optImgs.D || optImgs.d || data["OPTION D_IMAGE"] || data.option_d_image),
            E: getValidOptImg(optImgs.E || optImgs.e || data["OPTION E_IMAGE"] || data.option_e_image),
          };

          const rawTypeStr = String(data.question_type || data.QUESTION_TYPE || data.type || "").trim().toUpperCase();

          let options = [];
          let choiceIds = [];
          if (Array.isArray(data.CHOICES || data.choices) && (data.CHOICES || data.choices).length > 0) {
            const choicesArr = data.CHOICES || data.choices;
            options = choicesArr.map((c) => (typeof c === "string" ? c : c.text || ""));
            choiceIds = choicesArr.map((c, idx) => (typeof c === "object" && c !== null ? (c.id || c.choice_id || c.key || String.fromCharCode(65 + idx)) : String.fromCharCode(65 + idx)));
          } else if (data.options) {
            options = [
              data.options.A || data.options.a || "",
              data.options.B || data.options.b || "",
              data.options.C || data.options.c || "",
              data.options.D || data.options.d || "",
            ];
            choiceIds = ["A", "B", "C", "D"];
          }

          const hasValidOptions = options.some((opt) => opt && typeof opt === "string" && opt.trim() !== "") || Boolean(sanitizedOptionImages && Object.values(sanitizedOptionImages).some(Boolean));
          const isShort = rawTypeStr === "SHORT" || rawTypeStr === "SHORT_ANSWER" || rawTypeStr === "NUMERICAL" || !hasValidOptions;
          const finalType = isShort ? "SHORT" : "MCQ";

          setWsQuestion({
            question: data.question || data.QUESTION || data.question_text || "",
            options: isShort ? [] : options,
            choiceIds: isShort ? [] : choiceIds,
            type: finalType,
            questionType: finalType,
            isShort,
            imageUrls: sanitizedImageUrls,
            optionImages: isShort ? null : sanitizedOptionImages,
            questionId: data.question_id || data.QUESTION_ID || "",
          });

          setWsQuestionIndex(data.index);
          setWsTotalQuestions(data.total);
          setTimeRemaining(data.timer_seconds);

          setWsSelectedAnswer(null);
          setWsSubmitted(false);

          break;
        }

        case "answer_received":
          console.log("Answer received:", data);
          break;

        case "round_result":
          setWsLeaderboard(data.leaderboard || []);
          setAdminRoundResult(data || {});
          setShowAdminRoundResult(true);
          setTimeout(() => {
            setShowAdminRoundResult(false);
          }, 4000);
          break;

        case "quiz_ended":
          setWsQuizEnded(true);
          setWsFinalScores(data.final_scores || []);
          setShowAsyncLeaderboard(true);
          asyncLeaderBoardApi();
          break;

        case "lobby_update":
          setWsParticipants(data.participants || []);
          break;

        case "timer":
          setTimeRemaining(data.time);
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      console.log("❌ WebSocket disconnected");
    };

    setSocket(ws);
  };

  const asyncCurrentQuestion = asyncQuestions[asyncCurrentIndex];

  const joinRoomViaAsync = async () => {
    try {
      const res = await get(apiRoomAsyncQuestions(roomCode, mail));
      if (res?.data) {
        const data = res?.data;

        setIsAsyncQuiz(true);

        const formattedQuestions = data.questions.map((q) => {
          const rawTypeStr = String(
            q.QUESTION_TYPE || q.question_type || q.type || "",
          )
            .trim()
            .toUpperCase();

          const rawImages = Array.isArray(q.IMAGE_URLS)
            ? q.IMAGE_URLS
            : Array.isArray(q.image_urls)
            ? q.image_urls
            : Array.isArray(q.imageUrls)
            ? q.imageUrls
            : Array.isArray(q.question_images)
            ? q.question_images
            : Array.isArray(q.images)
            ? q.images
            : q.IMAGE_URL || q.image_url || q.image || q.diagram || q.question_image || q.figure
            ? [q.IMAGE_URL || q.image_url || q.image || q.diagram || q.question_image || q.figure]
            : [];
          const imageUrls = rawImages
            .map((img) => {
              if (!img) return "";
              let raw = typeof img === "object" ? (img.url || img.src || img.image_url || img.image || "") : img;
              if (typeof raw !== "string" || !raw.trim()) return "";
              return sanitizeImageUrl(raw) || raw;
            })
            .filter(Boolean);

          const rawOptImg = q.OPTION_IMAGES || q.option_images || q.optionImages || {};
          let optionImages = {
            A: getValidOptImg(
              rawOptImg.a ||
                rawOptImg.A ||
                q["OPTION A_IMAGE"] ||
                q.option_a_image ||
                q["OPTION A IMAGE"] ||
                q.option_a_img ||
                q.optionA_image ||
                q.optionAImage,
            ),
            B: getValidOptImg(
              rawOptImg.b ||
                rawOptImg.B ||
                q["OPTION B_IMAGE"] ||
                q.option_b_image ||
                q["OPTION B IMAGE"] ||
                q.option_b_img ||
                q.optionB_image ||
                q.optionBImage,
            ),
            C: getValidOptImg(
              rawOptImg.c ||
                rawOptImg.C ||
                q["OPTION C_IMAGE"] ||
                q.option_c_image ||
                q["OPTION C IMAGE"] ||
                q.option_c_img ||
                q.optionC_image ||
                q.optionCImage,
            ),
            D: getValidOptImg(
              rawOptImg.d ||
                rawOptImg.D ||
                q["OPTION D_IMAGE"] ||
                q.option_d_image ||
                q["OPTION D IMAGE"] ||
                q.option_d_img ||
                q.optionD_image ||
                q.optionDImage,
            ),
            E: getValidOptImg(
              rawOptImg.e ||
                rawOptImg.E ||
                q["OPTION E_IMAGE"] ||
                q.option_e_image ||
                q["OPTION E IMAGE"] ||
                q.option_e_img ||
                q.optionE_image ||
                q.optionEImage,
            ),
          };

          let options = [];
          let choiceIds = [];
          if (Array.isArray(q.CHOICES) && q.CHOICES.length > 0) {
            options = q.CHOICES.map((c) =>
              typeof c === "string" ? c : c.text || "",
            );
            choiceIds = q.CHOICES.map((c, idx) =>
              typeof c === "object" && c !== null
                ? c.id || c.choice_id || c.key || String.fromCharCode(65 + idx)
                : String.fromCharCode(65 + idx),
            );
            const choiceImgs = {};
            q.CHOICES.forEach((c, idx) => {
              if (typeof c === "object" && c.image_url) {
                const optLetter = String.fromCharCode(65 + idx);
                choiceImgs[optLetter] = getValidOptImg(c.image_url);
              }
            });
            if (Object.keys(choiceImgs).length > 0) {
              optionImages = { ...(optionImages || {}), ...choiceImgs };
            }
          } else {
            options = [
              q["OPTION A"] || q.option_a || q.optionA || "",
              q["OPTION B"] || q.option_b || q.optionB || "",
              q["OPTION C"] || q.option_c || q.optionC || "",
              q["OPTION D"] || q.option_d || q.optionD || "",
            ];
            choiceIds = ["A", "B", "C", "D"];
            if (q["OPTION E"] || q.option_e || q.optionE) {
              options.push(q["OPTION E"] || q.option_e || q.optionE);
              choiceIds.push("E");
            }
          }

          ["A", "B", "C", "D", "E"].forEach((letter, idx) => {
            if (!optionImages[letter] && options[idx] && isImgUrl(options[idx])) {
              optionImages[letter] = getValidOptImg(options[idx]);
            }
          });

          const hasValidOptions =
            options.some(
              (opt) => opt && typeof opt === "string" && opt.trim() !== "",
            ) || Boolean(optionImages && Object.values(optionImages).some(Boolean));
          const isShort =
            rawTypeStr === "SHORT" ||
            rawTypeStr === "SHORT_ANSWER" ||
            rawTypeStr === "NUMERICAL" ||
            !hasValidOptions;
          const finalType = isShort ? "SHORT" : "MCQ";

          return {
            ...q,
            question: q.QUESTION || q.question || q.question_text || "",
            options: isShort ? [] : options,
            choiceIds: isShort ? [] : choiceIds,
            type: finalType,
            questionType: finalType,
            isShort,
            questionId: q.QUESTION_ID || q.id || q.question_id || "",
            imageUrls,
            IMAGE_URLS: imageUrls,
            image_urls: imageUrls,
            CHOICES: q.CHOICES || [],
            optionImages: isShort ? null : optionImages,
          };
        });

        setAsyncQuestions(formattedQuestions);
        setWsTotalQuestions(data.total_questions);
        setAsyncCurrentIndex(0);
        setAsyncSelectedAnswers({});
        setShowReviewPage(false);
        setEditingFromReview(false);
        asyncQuestionStartTimeRef.current = Date.now();
        asyncQuestionTimesRef.current = {};
      }
    } catch (error) {
      console.log("Error comes from join room async", error);
    }
  };

  const recordAsyncTime = (idx) => {
    if (asyncQuestionStartTimeRef.current && idx !== null && idx !== undefined) {
      const elapsed = Date.now() - asyncQuestionStartTimeRef.current;
      if (elapsed > 0) {
        asyncQuestionTimesRef.current[idx] =
          (asyncQuestionTimesRef.current[idx] || 0) + elapsed;
      }
    }
  };

  const prevAsyncIndexRef = useRef(asyncCurrentIndex);
  const prevShowReviewRef = useRef(showReviewPage);

  useEffect(() => {
    if (!isAsyncQuiz) {
      asyncQuestionStartTimeRef.current = null;
      return;
    }

    if (showReviewPage) {
      if (!prevShowReviewRef.current && prevAsyncIndexRef.current !== null && prevAsyncIndexRef.current !== undefined) {
        recordAsyncTime(prevAsyncIndexRef.current);
      }
      asyncQuestionStartTimeRef.current = null;
    } else {
      if (prevShowReviewRef.current) {
        asyncQuestionStartTimeRef.current = Date.now();
      } else if (prevAsyncIndexRef.current !== asyncCurrentIndex) {
        recordAsyncTime(prevAsyncIndexRef.current);
        asyncQuestionStartTimeRef.current = Date.now();
      } else if (!asyncQuestionStartTimeRef.current) {
        asyncQuestionStartTimeRef.current = Date.now();
      }
    }

    prevAsyncIndexRef.current = asyncCurrentIndex;
    prevShowReviewRef.current = showReviewPage;
  }, [asyncCurrentIndex, showReviewPage, isAsyncQuiz]);

  const handleAsyncNext = () => {
    if (editingFromReview) {
      setEditingFromReview(false);
      setShowReviewPage(true);
      return;
    }

    if (asyncCurrentIndex === asyncQuestions.length - 1) {
      setShowReviewPage(true);
      return;
    }

    // normal next
    setAsyncCurrentIndex((prev) => prev + 1);
  };

  const handleAsyncAnswer = (index) => {
    setAsyncSelectedAnswers((prev) => ({
      ...prev,
      [asyncCurrentIndex]: index,
    }));
  };

  const enterFullScreen = () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen request error:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } catch (e) {
      console.log("Fullscreen error:", e);
    }
  };

  const joinRoom = async () => {
    const payload = {
      mail,
      nickname: nickName.trim(),
      room_code: roomCode.trim(),
    };
    if (!mail || !nickName.length || !roomCode.length) return;

    enterFullScreen();
    document.body.classList.add("hide_sidebar");
    try {
      const res = await post(API_JOIN_ROOM, payload);
      if (res) {
        console.log(res?.data);
        const { ok, mode } = res?.data;
        if (mode === "live") {
          setWaitingCall(true);
          // 🔥 ADD THIS LINE
          setTimeout(() => {
            connectWebSocket(roomCode, nickName);
          }, 500);
        } else if (mode === "async") {
          joinRoomViaAsync();
        }
      }
    } catch (error) {
      console.log("Error comes from create api", error);
    }
  };

  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [socket]);

  const handleWsAnswer = (answerValue) => {
    // prevent duplicate submit
    if (wsSubmitted) return;

    setWsSelectedAnswer(answerValue);

    let finalAns = "";
    if (wsQuestion?.isShort || wsQuestion?.type === "SHORT" || wsQuestion?.questionType === "SHORT") {
      finalAns = answerValue !== undefined && answerValue !== null ? String(answerValue).trim() : "";
    } else {
      if (typeof answerValue === "number") {
        if (Array.isArray(wsQuestion?.choiceIds) && wsQuestion.choiceIds[answerValue]) {
          finalAns = String(wsQuestion.choiceIds[answerValue]);
        } else {
          finalAns = ["A", "B", "C", "D"][answerValue] || String(answerValue);
        }
      } else {
        finalAns = String(answerValue).trim();
      }
    }

    socketRef.current?.send(
      JSON.stringify({
        event: "submit_answer",
        answer: finalAns,
        time_ms: 0,
      }),
    );

    setWsSubmitted(true);
  };

  const getQuizFinalResults = async () => {
    try {
      const res = await get(apiCodeJoinResults(roomCode));
      if (res?.data?.final_scores) {
        setWsQuizFinalResults(res?.data?.final_scores);
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  const asyncLeaderBoardApi = async () => {
    try {
      const res = await get(apiLeaderBoard(roomCode));
      if (res?.data?.leaderboard) {
        setAsyncLeaderboard(res?.data?.leaderboard);
        setShowAsyncLeaderboard(true);

        // optional
        setShowReviewPage(false);
        setIsAsyncQuiz(false);
      }
    } catch (error) {
      console.log("Error comes from async quiz submit api", error);
    }
  };

  const asyncQuizSubmit = async () => {
    if (!showReviewPage && asyncCurrentIndex !== null && asyncCurrentIndex !== undefined && asyncQuestionStartTimeRef.current) {
      const elapsed = Date.now() - asyncQuestionStartTimeRef.current;
      if (elapsed > 0) {
        asyncQuestionTimesRef.current[asyncCurrentIndex] =
          (asyncQuestionTimesRef.current[asyncCurrentIndex] || 0) + elapsed;
      }
      asyncQuestionStartTimeRef.current = Date.now();
    }

    const formattedAnswers = asyncQuestions.map((question, index) => {
      const selectedValue = asyncSelectedAnswers[index];
      let chosenAns = "";

      if (question.isShort || question.type === "SHORT" || question.questionType === "SHORT") {
        chosenAns = selectedValue !== undefined && selectedValue !== null ? String(selectedValue).trim() : "";
      } else {
        if (selectedValue !== undefined && selectedValue !== null) {
          if (typeof selectedValue === "number") {
            if (Array.isArray(question.choiceIds) && question.choiceIds[selectedValue]) {
              chosenAns = String(question.choiceIds[selectedValue]);
            } else {
              chosenAns = ["A", "B", "C", "D"][selectedValue] || String(selectedValue);
            }
          } else {
            chosenAns = String(selectedValue).trim();
          }
        }
      }

      return {
        question_id: question.questionId,
        chosen_answer: chosenAns,
        time_taken_ms: Math.round(asyncQuestionTimesRef.current[index] || 0),
      };
    });

    const payload = {
      mail: mail,
      answers: formattedAnswers,
    };

    console.log("ASYNC SUBMIT PAYLOAD", payload);

    try {
      const res = await post(apiAsyncSubmit(roomCode), payload);
      if (res) {
        asyncLeaderBoardApi();
      }
    } catch (error) {
      console.log("Error comes from async quiz submit api", error);
    }
  };

  const apiJoinRoomHistoryDetails = async () => {
    try {
      const res = await get(apiJoinRoomHistory(mail));
      if (res?.data?.sessions) {
        setQuizHistorySessions(res?.data?.sessions);
      }
    } catch (error) {
      console.log("Api error comes from api join room histroy", error);
    }
  };

  const leaderboardData =
    wsQuizEnded && wsQuizFinalResults?.length > 0
      ? wsQuizFinalResults
      : asyncLeaderboard;

  useEffect(() => {
    setSelectedSubject(searchParams.get("subject") || "");
    setSelectedChapters(
      searchParams.get("chapter") ? [searchParams.get("chapter")] : [],
    );
  }, [searchParams]);

  useEffect(() => {
    if (subject && chapter) {
      setQuizInitial(false);
    }
  }, [subject, chapter]);

  return {
    user,
    subjectRef,
    chapterRef,
    openDropdown,
    selectedChapter,
    selectedSubject,
    panelOpen,
    subjects,
    chapters,
    isStartEnabled,
    questionDetails,
    currentQuestion,
    selectedOption,
    questions,
    currentQIndex,
    isAnswered,
    isCorrect,
    markDetails,
    showMarks,
    isQuizActive,
    isLoading,
    selectChapters,
    mediums,
    mediumRef,
    selectedMedium,
    numberOfQuestion,
    mode,
    selectedQuestion,
    questionRef,
    hidePanals,
    submitLoading,
    timeRemaining,
    selectedAnswers,
    showPaletteModal,
    isReviewMode,
    showSetupPopup,
    // newfeaure
    quizInitial,
    joinQuiz,
    waitingCall,
    roomCode,
    nickName,
    players,
    wsQuestion,
    wsQuestionIndex,
    wsTotalQuestions,
    wsLeaderboard,
    wsFinalScores,
    wsParticipants,
    wsQuizStarted,
    wsQuizEnded,
    wsSelectedAnswer,
    wsReviewMode,
    wsQuizSubmitted,
    wsQuizFinalResults,
    isAsyncQuiz,
    asyncCurrentQuestion,
    asyncCurrentIndex,
    asyncSelectedAnswers,
    showReviewPage,
    asyncQuestions,
    leaderboardData,
    showAsyncLeaderboard,
    asyncLeaderboard,
    quizHistorySessions,
    showAsyncHistory,
    wsTimeRemaining,
    wsSubmitted,
    showAdminRoundResult,
    adminRoundResult,
    setShowAdminRoundResult,
    setWsSubmitted,
    setRoomDetails,
    setAsyncQuestions,
    setAsyncCurrentIndex,
    setAsyncSelectedAnswers,
    setShowReviewPage,
    setAsyncLeaderboard,
    setShowAsyncLeaderboard,
    setShowAsyncHistory,
    setIsAsyncQuiz,
    setWsTimeRemaining,
    handleWsAnswer,
    setWsFinalScores,
    setWsQuestion,
    setWsQuizStarted,
    setWsQuizEnded,
    asyncQuizSubmit,
    apiJoinRoomHistoryDetails,
    handleAsyncAnswer,
    handleAsyncNext,
    joinRoom,
    setWsReviewMode,
    setWsQuizSubmitted,
    setWsSelectedAnswer,
    setQuizInitial,
    setJoinQuiz,
    setPlayers,
    onChangeRoom,
    setWaitingCall,
    setTimeRemaining,
    setSelectedQuestion,
    setCurrentQIndex,
    setShowSetupPopup,
    setShowPaletteModal,
    setIsReviewMode,
    setShowMarks,
    setHidePanals,
    setSelectedChapters,
    onCancel,
    setPanelOpen,
    setOpenDropdown,
    setSelectedChapter,
    setSelectedSubject,
    onStartQuiz,
    handleNext,
    handlePrevious,
    handleOptionSelect,
    onSubmitQuiz,
    onRetakeQuiz,
    setIsQuizActive,
    getQuizDuration,
    setSelectedMedium,
    onToggleQuizBtn,
    getQuestionStatus,
  };
};

export default useHandler;
